import {
    useMutation,
    useQueryClient,
    type UseMutationOptions,
} from '@tanstack/react-query'

import { type WithNonNullable } from '@synapex/tools/typescript'
import { LinksQueryKeyConstants } from '../invite-links-group/invite-links-group.query.constants'
import {
    AnyLinkWithAnalyticsType,
    ICreative,
    QUERY_KEY,
} from './creatives.defs'
import { apiQueryKey } from '../'
import { WorkspaceDescriptor, WorkspaceType } from '@synapex/shared2/entities'
import {
    channelLinkMany,
    siteLinkMany,
} from '@/true-fsd/data/link/react-query/key-fn'

type MutationContext = {
    creativesBackup: ICreative[]
    linksBackup: AnyLinkWithAnalyticsType[]
}

export const CREATIVE_TYPE_CONTEXT = {
    channel: {
        groupsQueryKey: LinksQueryKeyConstants.GroupsChannels,
        linkIdProp: 'invite_link_id' as const,
    },
    site: {
        groupsQueryKey: LinksQueryKeyConstants.GroupsSites,
        linkIdProp: 'site_link_id' as const,
    },
} satisfies Record<WorkspaceType, object>

export const useCreativeMutationWithOptimisticCacheUpdate = <
    TVariables extends { creativeId: number },
    TData = unknown,
>(
    {
        workspace_type,
        workspace_id,
        mutationFn,
        creativeUpdater,
        linkUpdater,
    }: WorkspaceDescriptor & {
        groupId: number
        mutationFn: UseMutationOptions<
            TData,
            Error,
            TVariables,
            MutationContext
        >['mutationFn']
        creativeUpdater: (
            targetCreative: ICreative,
            isTargetCreativePublished: boolean,
            variables: TVariables
        ) => undefined | ICreative
        linkUpdater: (
            link: WithNonNullable<AnyLinkWithAnalyticsType, 'active_creative'>,
            updatedTargetCreative: ICreative | undefined,
            isTargetCreativePublished: boolean
        ) => AnyLinkWithAnalyticsType
    },
    opts: Pick<
        UseMutationOptions<TData, Error, TVariables>,
        'onError' | 'onSuccess'
    >
) => {
    const client = useQueryClient()

    const typeContext = CREATIVE_TYPE_CONTEXT[workspace_type]

    const linksQueryKey = {
        creatives: [QUERY_KEY.creatives, workspace_type, workspace_id] as const,
        links:
            workspace_type === 'site'
                ? siteLinkMany({ site_id: workspace_id }).queryKey
                : channelLinkMany({ channel_id: workspace_id }).queryKey,
    }

    return useMutation({
        mutationFn,
        onMutate: async (variables: TVariables) => {
            await client.cancelQueries({
                queryKey: linksQueryKey.creatives,
            })
            const creativesBackup =
                client.getQueryData<ICreative[]>(linksQueryKey.creatives) ?? []

            const targetCreative = creativesBackup.find(
                (creative) => creative.id === variables.creativeId
            )

            if (targetCreative) {
                const isTargetCreativePublished =
                    targetCreative.active_in_links.some(
                        (link) => link.status === 'published'
                    )

                const updatedTargetCreative = creativeUpdater(
                    targetCreative,
                    isTargetCreativePublished,
                    variables
                )

                client.setQueryData<ICreative[]>(
                    linksQueryKey.creatives,
                    (currentCreatives) => {
                        if (!currentCreatives) {
                            return undefined
                        }

                        return currentCreatives.reduce<ICreative[]>(
                            (result, creative) => {
                                if (creative.id === variables.creativeId) {
                                    if (updatedTargetCreative) {
                                        result.push({
                                            ...updatedTargetCreative,
                                        })
                                    }
                                } else {
                                    result.push(creative)
                                }
                                return result
                            },
                            []
                        )
                    }
                )

                const linksBackup =
                    client.getQueryData<AnyLinkWithAnalyticsType[]>(
                        linksQueryKey.links
                    ) ?? []

                client.setQueryData<AnyLinkWithAnalyticsType[]>(
                    linksQueryKey.links,
                    (currentLinks) => {
                        if (!currentLinks) {
                            return undefined
                        }

                        return currentLinks.map((link) => {
                            const { active_creative, ...linkRest } = link
                            if (
                                active_creative &&
                                active_creative.id === targetCreative.id
                            ) {
                                return linkUpdater(
                                    { ...linkRest, active_creative },
                                    updatedTargetCreative,
                                    isTargetCreativePublished
                                )
                            }

                            return link
                        })
                    }
                )

                return { linksBackup, creativesBackup }
            }
        },
        onSettled: () => {
            void client.invalidateQueries({
                queryKey: [QUERY_KEY.creatives, workspace_type, workspace_id],
            })
            void client.invalidateQueries({
                queryKey: [typeContext.groupsQueryKey, workspace_id],
            })
            void client.invalidateQueries({
                queryKey: linksQueryKey.links,
            })
            void client.invalidateQueries({
                queryKey: apiQueryKey.markingFormData({
                    workspace_type: workspace_type,
                    workspace_id: workspace_id,
                }),
            })
        },
        onSuccess: opts.onSuccess,
        onError: (
            error: Error,
            variables: TVariables,
            context: MutationContext | null | undefined
        ) => {
            console.error(error)

            if (context) {
                client.setQueryData(
                    linksQueryKey.creatives,
                    context.creativesBackup
                )
                client.setQueryData<AnyLinkWithAnalyticsType[]>(
                    linksQueryKey.links,
                    context.linksBackup
                )
            }

            if (opts.onError) {
                opts.onError(error, variables, context)
            }
        },
    })
}
